import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/store';
import { savePaidolSessionId } from 'app/store/authSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import MobileBottomSheet from 'app/pages/mobile/components/mobile-option-menu/MobileBottomSheet';
import MobileOptionList, {
  OptionItem,
} from 'app/pages/mobile/components/mobile-option-menu/MobileOptionList';
import MobileSearchBar from 'app/pages/mobile/components/MobileSearchBar';
import { NAV_ROUTES } from 'app/pages/mobile/utils/constants';

interface MobileCompanySwitcherProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Mobile company (paidol) switcher, per the Figma design: a bottom sheet titled
 * "Company switcher" with a search field and the user's companies, the active
 * one highlighted. Rotates the active paidol via the shared
 * `savePaidolSessionId` thunk (backend session rotation + JWT refresh +
 * paidol-scoped Redux reset), then navigates to the mobile home route.
 *
 * v1 lists the user's own memberships (`paidolUsers`) with a client-side filter.
 * Support-user server search + scroll pagination (mirroring the web switcher's
 * `searchPaidolsAsSupport`) is a later pass and drops into the same
 * `MobileOptionList` (it already supports hasMore/onLoadMore).
 */
function MobileCompanySwitcher({ open, onClose }: MobileCompanySwitcherProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { paidolUsers, selectedPaidolId, isSwitching } = useAppSelector(selectUserCompanies);
  const [query, setQuery] = useState('');

  const options = useMemo<OptionItem[]>(() => {
    const trimmed = query.trim().toLowerCase();
    return paidolUsers
      .map((pu) => ({
        id: pu.paidol?.id ?? pu.paidol_id ?? undefined,
        name: pu.paidol?.name ?? '',
        disabled: isSwitching,
      }))
      .filter((option) => Boolean(option.id) && Boolean(option.name))
      .filter((option) => !trimmed || option.name.toLowerCase().includes(trimmed));
  }, [paidolUsers, isSwitching, query]);

  // Reset the search query on every close path so reopening the sheet starts
  // fresh (the component stays mounted, so state persists otherwise).
  const handleClose = () => {
    setQuery('');
    onClose();
  };

  const handleSelect = async (option: OptionItem) => {
    if (!option.id || option.id === selectedPaidolId) {
      handleClose();
      return;
    }

    try {
      await dispatch(savePaidolSessionId(option.id)).unwrap();
      handleClose();
      // Entity routes from the previous paidol often don't exist in the new
      // one; land on the mobile home tab instead.
      navigate(NAV_ROUTES.home);
    } catch {
      // The SEE-861 backend membership check rejects with 403 for non-members;
      // any thunk rejection lands here.
      enqueueSnackbar(t('mobileCompanySwitcherError'), { variant: 'error' });
    }
  };

  return (
    <MobileBottomSheet
      open={open}
      onClose={handleClose}
      title={t('mobileCompanySwitcherTitle')}
      showDragHandle
      hideCloseButton
    >
      <Box sx={{ mb: 1, flexShrink: 0 }}>
        <MobileSearchBar value={query} onChange={setQuery} />
      </Box>
      <MobileOptionList
        options={options}
        onSelect={handleSelect}
        selectedId={selectedPaidolId}
        isLoading={isSwitching}
      />
    </MobileBottomSheet>
  );
}

export default MobileCompanySwitcher;
